/**
 * Autouzupełnianie miejscowości po wpisaniu kodu pocztowego (Adresy.app via backend).
 * Polski kod: wyłącznie cyfry, dokładnie 5 (zapis kanoniczny XX-XXX).
 */

function getConfig() {
    return window.labnetPostalCodeLookup || {};
}

/** Wyciąga max 5 cyfr z wartości (ignoruje myślnik). */
function extractPostalDigits(raw) {
    return String(raw || '').replace(/\D/g, '').slice(0, 5);
}

/**
 * Normalizuje do XX-XXX albo null — wyłącznie 5 cyfr (myślnik opcjonalny przy wpisie).
 */
function normalizePostalCode(raw) {
    const value = String(raw || '').trim();
    if (value === '') {
        return null;
    }
    if (/[^\d\-]/.test(value)) {
        return null;
    }
    const digits = extractPostalDigits(value);
    if (digits.length !== 5) {
        return null;
    }
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

function getWireModel(input) {
    return (
        input.getAttribute('wire:model') ||
        input.getAttribute('wire:model.live') ||
        input.getAttribute('wire:model.defer') ||
        input.getAttribute('wire:model.blur') ||
        null
    );
}

function syncLivewireValue(input, value) {
    const wireModel = getWireModel(input);
    if (!wireModel || !window.Livewire) {
        return;
    }
    const root = input.closest('[wire\\:id]');
    if (!root) {
        return;
    }
    const component = window.Livewire.find(root.getAttribute('wire:id'));
    if (component) {
        component.set(wireModel, value);
    }
}

function setInputValue(input, value) {
    if (!input || value === undefined || value === null) {
        return;
    }
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncLivewireValue(input, value);
}

function setInputValueSilent(input, value) {
    if (!input || value === undefined || value === null) {
        return;
    }
    input.value = value;
    syncLivewireValue(input, value);
}

function getLastFetchedCode(postalInput) {
    return postalInput._pclLastFetchedCode ?? null;
}

function setLastFetchedCode(postalInput, code) {
    postalInput._pclLastFetchedCode = code;
}

/**
 * Ustawia parę kod + miejscowość bez lookup (np. kopiowanie z innego bloku formularza).
 */
function fillPostalCodePairWithoutLookup(postalInput, postalValue, localityInput, localityValue) {
    if (localityInput) {
        setInputValueSilent(localityInput, localityValue ?? '');
    }
    if (postalInput) {
        setInputValueSilent(postalInput, postalValue ?? '');
        postalInput._skipNextLookup = true;
        setLastFetchedCode(postalInput, normalizePostalCode(postalValue ?? '') || null);
    }
}

function resolveLocalityTarget(postalInput) {
    const explicit = postalInput.dataset.localityTarget;
    if (explicit) {
        return document.querySelector(explicit);
    }

    const id = postalInput.id || '';
    if (id.includes('kodpocztowy')) {
        return document.getElementById(id.replace('kodpocztowy', 'miejscowosc'));
    }

    const form = postalInput.closest('form');
    if (!form) {
        return null;
    }

    return form.querySelector('[id*="miejscowosc"], [wire\\:model*="miejscowosc"]');
}

function resolveLookupUrl(postalInput) {
    if (postalInput.dataset.postalCodeLookupUrl) {
        return postalInput.dataset.postalCodeLookupUrl;
    }
    const form = postalInput.closest('form, [data-postal-code-lookup-url]');
    if (form?.dataset.postalCodeLookupUrl) {
        return form.dataset.postalCodeLookupUrl;
    }
    const cfg = getConfig();
    return cfg.url || cfg.publicUrl || '/postal-code/lookup';
}

function ensureDropdown(postalInput) {
    let dropdown = postalInput._postalCodeDropdown;
    if (dropdown?.isConnected) {
        return dropdown;
    }

    dropdown = document.createElement('div');
    dropdown.className = 'postal-code-lookup-dropdown list-group shadow-sm';
    dropdown.setAttribute('role', 'listbox');
    dropdown.hidden = true;

    const wrapper = postalInput.closest('.postal-code-lookup-wrap') || postalInput.parentElement;
    if (wrapper && getComputedStyle(wrapper).position === 'static') {
        wrapper.classList.add('postal-code-lookup-wrap');
    } else if (wrapper) {
        wrapper.classList.add('postal-code-lookup-wrap');
    }

    wrapper.appendChild(dropdown);
    postalInput._postalCodeDropdown = dropdown;

    return dropdown;
}

function hideDropdown(postalInput) {
    const dropdown = postalInput._postalCodeDropdown;
    if (dropdown) {
        dropdown.hidden = true;
        dropdown.replaceChildren();
    }
}

function showLocalityOptions(postalInput, localities, onSelect) {
    const dropdown = ensureDropdown(postalInput);
    const cfg = getConfig();
    dropdown.replaceChildren();

    localities.forEach((name) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'list-group-item list-group-item-action py-2';
        item.textContent = name;
        item.setAttribute('role', 'option');
        item.addEventListener('mousedown', (event) => {
            event.preventDefault();
        });
        item.addEventListener('click', () => {
            onSelect(name);
            hideDropdown(postalInput);
        });
        dropdown.appendChild(item);
    });

    dropdown.hidden = localities.length === 0;
}

async function fetchLocality(postalCode, postalInput) {
    const url = resolveLookupUrl(postalInput);
    const params = new URLSearchParams({ kod: postalCode });
    const response = await fetch(`${url}?${params.toString()}`, {
        headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
    });

    const result = await response.json();
    if (!response.ok || !result.ok) {
        throw new Error(result.error || 'Lookup failed');
    }

    return result.data || {};
}

function bindPostalCodeInput(postalInput) {
    if (postalInput.dataset.pclBound === '1') {
        return;
    }
    postalInput.dataset.pclBound = '1';
    postalInput.classList.add('js-postal-code-lookup');

    let debounceTimer = null;

    const runLookup = async () => {
        if (postalInput._skipNextLookup) {
            postalInput._skipNextLookup = false;
            hideDropdown(postalInput);
            const normalized = normalizePostalCode(postalInput.value);
            if (normalized) {
                setLastFetchedCode(postalInput, normalized);
            }
            return;
        }

        const normalized = normalizePostalCode(postalInput.value);
        const localityInput = resolveLocalityTarget(postalInput);
        const cfg = getConfig();

        if (!normalized) {
            hideDropdown(postalInput);
            setLastFetchedCode(postalInput, null);
            return;
        }

        if (normalized === getLastFetchedCode(postalInput)) {
            return;
        }

        postalInput.classList.add('postal-code-lookup--loading');

        try {
            const data = await fetchLocality(normalized, postalInput);
            setLastFetchedCode(postalInput, normalized);

            if (!data.found) {
                hideDropdown(postalInput);
                return;
            }

            const applyLocality = (name) => {
                if (localityInput) {
                    setInputValue(localityInput, name);
                }
            };

            if (data.unique && data.miejscowosc) {
                applyLocality(data.miejscowosc);
                hideDropdown(postalInput);
                return;
            }

            const options = Array.isArray(data.miejscowosci) ? data.miejscowosci : [];
            if (options.length === 0) {
                hideDropdown(postalInput);
                return;
            }

            if (options.length === 1) {
                applyLocality(options[0]);
                hideDropdown(postalInput);
                return;
            }

            showLocalityOptions(postalInput, options, applyLocality);
        } catch (error) {
            hideDropdown(postalInput);
            if (window.Swal && error.message && error.message !== 'Lookup failed') {
                window.Swal.fire({
                    icon: 'info',
                    title: cfg.i18n?.notFound || '',
                    text: error.message,
                    timer: 2500,
                    showConfirmButton: false,
                });
            }
        } finally {
            postalInput.classList.remove('postal-code-lookup--loading');
        }
    };

    const scheduleLookup = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(runLookup, 350);
    };

    const applyFormatAndValidity = () => {
        const raw = (postalInput.value || '').trim();
        const cfg = getConfig();
        const invalidMsg = cfg.i18n?.invalid || '';

        if (raw === '') {
            postalInput.setCustomValidity('');
            return;
        }

        const normalized = normalizePostalCode(raw);
        if (!normalized) {
            postalInput.setCustomValidity(invalidMsg || 'Invalid postal code');
            return;
        }

        if (normalized !== raw) {
            // Silent — bez eventu input, żeby filtr cyfr nie zdjął myślnika.
            setInputValueSilent(postalInput, normalized);
        }
        postalInput.setCustomValidity('');
    };

    /** Podczas wpisywania: tylko cyfry, max 5 (gotowe XX-XXX zostawiamy). */
    const restrictDigitsOnInput = () => {
        const raw = postalInput.value || '';
        if (normalizePostalCode(raw) === raw) {
            postalInput.setCustomValidity('');
            scheduleLookup();
            return;
        }

        const digits = extractPostalDigits(raw);
        if (raw !== digits) {
            setInputValue(postalInput, digits);
        }
        postalInput.setCustomValidity('');
        scheduleLookup();
    };

    postalInput.setAttribute('inputmode', 'numeric');
    postalInput.setAttribute('maxlength', '6'); // XX-XXX po blur
    postalInput.setAttribute('pattern', '[0-9]{2}-?[0-9]{3}|[0-9]{5}');

    postalInput.addEventListener('input', restrictDigitsOnInput);
    postalInput.addEventListener('blur', () => {
        applyFormatAndValidity();
        setTimeout(() => hideDropdown(postalInput), 180);
        // Puste / niepełne pole — bez requestu (unikamy 400 i szumu w logach).
        if (normalizePostalCode(postalInput.value)) {
            scheduleLookup();
        }
    });
}

function initPostalCodeLookup(root = document) {
    root.querySelectorAll('input.js-postal-code-lookup').forEach(bindPostalCodeInput);

    root.querySelectorAll('input[autocomplete="postal-code"]:not(.js-postal-code-lookup)').forEach((input) => {
        input.classList.add('js-postal-code-lookup');
        bindPostalCodeInput(input);
    });

    // Wszystkie pola kodu pocztowego w aplikacji (także bez autocomplete / name).
    root.querySelectorAll('input[name*="kodpocztowy"]:not(.js-postal-code-lookup), input[id*="kodpocztowy"]:not(.js-postal-code-lookup)').forEach((input) => {
        input.classList.add('js-postal-code-lookup');
        bindPostalCodeInput(input);
    });
}

module.exports = {
    initPostalCodeLookup,
    bindPostalCodeInput,
    fillPostalCodePairWithoutLookup,
    normalizePostalCode,
};
